'use client';
import { ErrorState } from '@repo/ui/src/components/Form';
import useCurrentUrl from '@repo/ui/src/hooks/useCurrentUrl';
import { ModalID, useModal } from '@repo/ui/src/modules/UI';
import React from 'react';
import Loading from '@components/Loading';
import LoadingLogo from '@components/Loading/LoadingLogo';
import useTranslation from '@repo/ui/src/context/Translation/useTranslation';
import useBasket from '@hooks/basket/useBasket';
import { useCustomer } from '@hooks/customer';
import useUpdateBasket from '@hooks/basket/useUpdateBasket';
import FlameIcon from '@repo/ui/src/components/Icons/FlameIcon';
import Button, { ButtonVariant } from '@repo/ui/src/components/Button';
import { Anchor } from '@repo/ui/src/components/Anchor';
import useGoToCheckout from '@hooks/basket/useGoToCheckout';
import { CartBody, CartFooter, CartOrderSummary } from '@repo/ui/src/components/Cart';
import { Markdown } from '@repo/ui/src/components/Markdown';
import Image from '@repo/ui/src/components/Image';
import { buildImageSrc } from '@repo/ui/src/functions/image-functions-aspect-ratio';
import { ImageProps } from '@repo/ui/src/types/image';
import { basketToCartLineItems, basketToCartTotals, getOutOfStockIds } from './helpers';

interface CartPageViewProps {
  outOfStockItems?: Array<string>;
}

const CartPageView = ({ outOfStockItems }: CartPageViewProps) => {
  const { data: basket, isLoading } = useBasket();
  const { locale, push } = useCurrentUrl();
  const [checkoutError, setCheckoutError] = React.useState(false);
  const [updatingItemId, setUpdatingItemId] = React.useState<string | undefined>();
  const { push: pushCheckoutLoader, close: closeCheckoutLoader } = useModal(ModalID.checkoutLoader);
  const { push: pushLoginModal } = useModal(ModalID.login);
  const { data: user } = useCustomer();
  const { trigger: goToCheckout } = useGoToCheckout();
  const { trigger: updateBasket } = useUpdateBasket();
  const { t } = useTranslation();

  const lineItemMap = React.useMemo(
    () => new Map((basket?.lineItems ?? []).map((li) => [li.id, li])),
    [basket?.lineItems],
  );

  const handleQuantityChange = React.useCallback(
    (id: string, quantity: number) => {
      const lineItem = lineItemMap.get(id);
      if (!lineItem) return;
      setUpdatingItemId(id);
      updateBasket({ lineItem, quantity }).finally(() => setUpdatingItemId(undefined));
    },
    [lineItemMap, updateBasket],
  );

  const handleRemove = React.useCallback(
    (id: string) => {
      const lineItem = lineItemMap.get(id);
      if (!lineItem) return;
      setUpdatingItemId(id);
      updateBasket({ lineItem, quantity: 0 }).finally(() => setUpdatingItemId(undefined));
    },
    [lineItemMap, updateBasket],
  );

  const { totalItems = 0 } = basket || {};
  const isEmpty = !totalItems;

  const handleCheckoutFlow = async () => {
    await goToCheckout();
    pushCheckoutLoader();
    const result = await fetch(`/${locale}/checkout`);
    if (result.redirected) {
      closeCheckoutLoader();
      setCheckoutError(true);
      return false;
    }
    push(`/${locale}/checkout`);
    return true;
  };

  const navigateToCheckout = async () => {
    if (!user) {
      pushLoginModal({
        isCheckout: true,
        onSuccess: handleCheckoutFlow,
      });
      return;
    }
    await handleCheckoutFlow();
  };

  const outOfStockIds = getOutOfStockIds(basket, outOfStockItems);
  const disabled = outOfStockIds != null && outOfStockIds.length > 0;

  const cartPromotionalMarkdown = t('cart.cartPromotionalMarkdown', undefined, { default: 'none' });
  const cartPromotionalIcon = t<ImageProps>('cart.cartPromotionalIcon', undefined, {
    returnObjects: true,
    default: undefined,
  });
  const promotionalImageUrl = buildImageSrc(cartPromotionalIcon?.image, 200, 0, 0);
  const promotionalNode =
    cartPromotionalMarkdown !== 'none' ? (
      <div className="bg-warm-200 p-6 mt-3 flex gap-4">
        <div className="w-20">
          <Image
            src={promotionalImageUrl}
            height={30}
            width={30}
            alt={cartPromotionalIcon?.altTag}
          />
        </div>
        <Markdown>{cartPromotionalMarkdown}</Markdown>
      </div>
    ) : undefined;

  if (isLoading) {
    return <Loading />;
  }

  const mappedLineItems = basket ? basketToCartLineItems(basket) : [];
  const mappedTotals = basket
    ? basketToCartTotals(basket)
    : { subtotal: 0, shippingTotal: 0, tax: 0, total: 0, totalItems: 0, currency: 'USD' };

  return (
    <div className="grid min-h-screen md:grid-cols-[2fr_1fr]">
      <div className="flex px-6 mt-5 md:mt-20 justify-center">
        <div className="max-w-[52rem] w-full">
          {checkoutError && (
            <ErrorState
              text={t('cart.errorStateText', undefined, {
                default: 'Something went wrong. Please try again.',
              })}
            />
          )}
          <div className="md:ml-6 flex-1">
            <div className="flex">
              <FlameIcon width={44} outline className="md:mt-4 mr-3 md:mr-6" />
              <div>
                <h1 className="tra-text-web-heading-3 mb-3">
                  {t('cart.title', undefined, { default: 'Your Cart' })}
                </h1>
                <p className="tra-text-web-body-m font-bold mb-6">
                  {t('cart.cartSubtitle', undefined, {
                    default: "You're almost ready to get cooking.",
                  })}
                </p>
              </div>
            </div>
            <div className="md:ml-16">
              <CartBody
                lineItems={mappedLineItems}
                isLoading={isLoading}
                isEmpty={isEmpty}
                outOfStockIds={outOfStockIds}
                updatingItemId={updatingItemId}
                onQuantityChange={handleQuantityChange}
                onRemove={handleRemove}
                additionalItemsSlot={undefined}
                showSummary={false}
                emptyMessage={t('cart.cartEmptyMessage', undefined, {
                  default: 'Your cart is currently empty.',
                })}
                outOfStockMessage={t('cart.outOfStockText', undefined, {
                  default:
                    'This item is out of stock. Remove from your cart to complete your purchase.',
                })}
                removeLabel={t('cart.cartRemoveText', undefined, { default: 'Remove' })}
                outOfStockLabel={t('productDetailPageContent.outOfStockText', undefined, {
                  default: 'Out of Stock',
                })}
              />
            </div>
          </div>
        </div>
      </div>

      <div className="bg-gray-100 flex flex-col items-start justify-start">
        <div className="w-full mt-20 mb-6 px-8 sticky top-[calc(var(--navTop)_+_1rem)]">
          <h1 className="tra-text-web-heading-4 mb-6 text-2xl font-bold">
            {t('orderSummary.orderSummaryTitle', undefined, { default: 'ORDER SUMMARY' })}
          </h1>
          <hr className="w-full border-warm-200" />
          <div className="my-5">
            <CartOrderSummary
              totals={mappedTotals}
              promotionalNode={promotionalNode}
              subtotalLabel={t(
                'orderSummary.subtotalItems',
                { totalItems: basket?.totalItems ?? 0 },
                { default: `Subtotal ({{totalItems}} items)` },
              )}
              shippingLabel={t('cart.shipping', undefined, { default: 'Shipping' })}
              shippingFreeLabel={t('addToCartWindowContent.shippingFreeLabel', undefined, {
                default: 'Free',
              })}
              taxLabel={t('cart.estimatedTax', undefined, { default: 'Estimated Tax' })}
              taxFreeLabel={t('addToCartWindowContent.taxFreeLabel', undefined, { default: '-' })}
              isCartPage
            />
          </div>
          <hr className="w-full border-warm-200" />
          <div className="mt-5 w-full">
            <CartFooter
              isLoading={isLoading}
              isEmpty={isEmpty}
              total={mappedTotals.total}
              currency={mappedTotals.currency}
              locale={locale}
              disabled={disabled}
              onCheckout={navigateToCheckout}
              loadingNode={<LoadingLogo small />}
            />
          </div>
          <Button
            variant={ButtonVariant.knife}
            full
            size="small"
            className="justify-center mb-8"
            link="/"
            as={Anchor}
          >
            {t('cart.continueShopping', undefined, { default: 'Continue Shopping' })}
          </Button>
          <hr className="w-full border-warm-200" />
        </div>
      </div>
    </div>
  );
};

export default CartPageView;
